import React, { FC, useMemo } from "react";

import { Avatar, Box, Typography } from "@mui/material";
import { theme } from "@ovou-inc/ovou-ui";
import { IContactInfo } from "../types/Contact";
import { generateMediaPath, getLinkedInId } from "utils";
import { DIGITAL_OCEAN_URL } from "../../../constants";
import { Flex } from "@chakra-ui/react";
import LinkedInColorIcon from "../../../components/Icon/svg/LinkedInColorIcon";
import { EnrichStatus, EnrichStatusType } from "api/enrich";
import EmailMissing from "./EmailMissing";

interface ITableContactNameColumnProps {
  contactInfo: IContactInfo;
  enrichStatus: EnrichStatusType;
  canEnrich: boolean;
}

const getContactLabel = (contact: IContactInfo): string => {
  if (contact?.full_name) {
    return contact.full_name;
  }

  if (contact?.email) {
    return contact.email;
  }

  if (contact?.linkedin_url) {
    return getLinkedInId(contact.linkedin_url);
  }

  return "Unknown Contact";
};

export const isEnrichmentCompletedWithoutEmail = (
  email: string,
  enrichStatus?: string,
): boolean => {
  const hasNoEmails = !email;
  const enrichmentWasAttempted =
    enrichStatus === EnrichStatus.COMPLETED ||
    enrichStatus === EnrichStatus.FAILED;

  return hasNoEmails && enrichmentWasAttempted;
};

export const TableContactNameColumn: FC<ITableContactNameColumnProps> = ({
  contactInfo,
  enrichStatus,
  canEnrich,
}) => {
  if (!contactInfo) {
    return <Box>No contact data</Box>;
  }

  const showCustomIcon = useMemo(() => {
    return (
      !contactInfo.photo_url &&
      !contactInfo.full_name &&
      !contactInfo.email &&
      contactInfo.linkedin_url &&
      contactInfo.linkedin_url !== ""
    );
  }, [contactInfo]);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
          opacity: !contactInfo.email ? 0.5 : 1,
        }}
      >
        {showCustomIcon ? (
          <Flex
            backgroundColor="gray.100"
            borderRadius="50%"
            width="48px"
            height="48px"
            alignItems="center"
            justifyContent="center"
          >
            <LinkedInColorIcon boxSize="24px" />
          </Flex>
        ) : (
          <Avatar
            sx={{ width: 48, height: 48 }}
            src={
              contactInfo.photo_url
                ? generateMediaPath(contactInfo.photo_url, DIGITAL_OCEAN_URL)
                : ""
            }
          />
        )}

        <Box flexDirection="column">
          <Typography
            fontWeight="bold"
            fontSize="0.875rem"
            color={theme.palette.brand.primary.black}
          >
            {getContactLabel(contactInfo)}
          </Typography>
          <Typography
            fontSize="0.875rem"
            color={theme.palette.brand.primary.black}
            style={{ whiteSpace: "normal", wordBreak: "break-all" }}
          >
            {contactInfo.email}
          </Typography>
          {contactInfo.phone && (
            <Typography
              fontSize="0.875rem"
              color={theme.palette.ui.greys.grey1}
            >
              {contactInfo.phone}
            </Typography>
          )}
        </Box>
      </Box>
      {!contactInfo.email && (
        <EmailMissing
          isEnrichmentCompletedWithoutEmail={isEnrichmentCompletedWithoutEmail(
            contactInfo.email,
            enrichStatus,
          )}
          canEnrich={canEnrich}
        />
      )}
    </Box>
  );
};
